import { AsyncLoadingState } from "@/features/dashboard/enums";
import { Alert } from "@/models/Alerts";
import { postArchiveAlert } from "@/services/alerts/mutation/archiveAlert";
import { fetchAlerts } from "@/services/alerts/query/fetchAlerts";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import AlertService from "@/services/alerts/AlertsService";

export interface AlertsState {
  loading: AsyncLoadingState;
  alerts: Alert[];
  allAlerts: {
    [classroomId: string]: Alert[];
  };
  activeAlert: Alert | null;
  classroomId: number | null;
  error: string | null;
}

export const initialAlertsState: AlertsState = {
  alerts: [],
  allAlerts: {},
  activeAlert: null,
  classroomId: null,
  loading: AsyncLoadingState.NONE,
  error: null,
};

export const getAlerts = createAsyncThunk<any, void>(
  "alerts/fetchAlerts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAlerts();
      return response;
    } catch (error: any) {
      console.error("Error fetching alerts data", error);
      return rejectWithValue(error.message);
    }
  }
);

export const archiveAlert = createAsyncThunk<any, { alertId: string }>(
  "alerts/archiveAlert",
  async ({ alertId }, { rejectWithValue }) => {
    try {
      const response = await postArchiveAlert({
        id: alertId,
        isArchived: true,
      });
      return { response, alertId };
    } catch (error: any) {
      console.error("Error acknowledging an alert", error);
      return rejectWithValue(error.message);
    }
  }
);

const AlertsSlice = createSlice({
  name: "alerts",
  initialState: initialAlertsState,
  reducers: {
    setActiveAlert(state, action: PayloadAction<Alert>) {
      if (action.payload) {
        state.activeAlert = {
          ...action.payload,
        };
      } else {
        state.activeAlert = null;
      }
    },
    // Add a local-only reducer for snoozing alerts
    snoozeAlert(state, action: PayloadAction<{ alertId: string }>) {
      // Simply remove the alert from the state without recording it as "snoozed"
      if (state.classroomId) {
        state.allAlerts[state.classroomId] = state.allAlerts[
          state.classroomId
        ].filter(alert => alert.id !== action.payload.alertId);
        state.alerts = state.allAlerts[state.classroomId];
      }
    },
    setActiveClassroom(state, action: PayloadAction<{ classroomId: number }>) {
      const { classroomId } = action.payload;
      state.classroomId = classroomId;

      if (state.allAlerts[classroomId]) {
        state.alerts = state.allAlerts[classroomId];
      } else {
        state.alerts = [];
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getAlerts.fulfilled, (state, action) => {
        state.loading = AsyncLoadingState.SUCCESS;
        if (action.payload && Array.isArray(action.payload)) {
          state.allAlerts = AlertService.processAlerts(
            AlertService.parseAlerts(action.payload)
          );
          if (state.classroomId && state.allAlerts[state.classroomId]) {
            state.alerts = state.allAlerts[state.classroomId];
          }
        }
      })
      .addCase(getAlerts.pending, state => {
        state.loading = AsyncLoadingState.LOADING;
      })
      .addCase(getAlerts.rejected, (state, action) => {
        state.loading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      })
      .addCase(archiveAlert.fulfilled, (state, action) => {
        // TODO: we need to have a queue for this,
        // because a user can change classroomId while acknowledging alerts
        state.loading = AsyncLoadingState.SUCCESS;
        if (action.payload && action.payload.alertId && state.classroomId) {
          state.allAlerts[state.classroomId] = state.allAlerts[
            state.classroomId
          ].filter(alert => alert.id !== action.payload.alertId);
          state.alerts = state.allAlerts[state.classroomId];
        }
      })
      .addCase(archiveAlert.pending, state => {
        state.loading = AsyncLoadingState.LOADING;
      })
      .addCase(archiveAlert.rejected, (state, action) => {
        state.loading = AsyncLoadingState.ERROR;
        state.error = action.payload as string;
      });
  },
});

export const { setActiveAlert, snoozeAlert, setActiveClassroom } =
  AlertsSlice.actions;
export const selectAlertsData = (state: any) => state.alerts as AlertsState;
export default AlertsSlice.reducer;
